import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import useMediaQuery from '../../hooks/useMediaQuery';
import styles from './StatusTooltip.module.css';

const EDGE_PADDING = 12;

/* Status badges drop their text label on narrow screens and show only the
   coloured dot, which leaves the status unreadable. This wraps such a badge so
   a tap names it, in the same bubble style as InfoTooltip.
   It is deliberately inert above `maxWidth`: there the label is already on
   screen, so a tooltip would only repeat it — and leaving the wrapper inert
   keeps the click on rows that are themselves clickable. */
function StatusTooltip({ label, maxWidth = 767, children }) {
  const [pos, setPos] = useState(null);
  const wrapRef   = useRef(null);
  const bubbleRef = useRef(null);
  const isMobile  = useMediaQuery(`(max-width: ${maxWidth}px)`);

  useEffect(() => {
    if (!pos) return undefined;
    const close = () => setPos(null);
    document.addEventListener('click', close);
    document.addEventListener('touchstart', close);
    /* The bubble is portalled to <body> with viewport coordinates, so it does
       not travel with the dot once anything moves — it would end up pointing
       at unrelated content. On a phone there is no pointer to leave, so
       scrolling is how you dismiss it. Capture also catches a table or drawer
       scrolling under it, not just the page. */
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('touchstart', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos]);

  /* Rotating to landscape can cross the breakpoint while a bubble is open. */
  useEffect(() => {
    if (!isMobile) setPos(null);
  }, [isMobile]);

  /* The bubble is centred on the dot, which pushes it off screen for a badge
     in the last column. Measure it once it exists, pull it back inside the
     viewport, and slide the caret the other way so it still points at the dot. */
  useLayoutEffect(() => {
    if (!pos || pos.settled || !bubbleRef.current) return;
    const half = bubbleRef.current.offsetWidth / 2;
    const left = Math.max(
      EDGE_PADDING + half,
      Math.min(window.innerWidth - EDGE_PADDING - half, pos.anchor)
    );
    setPos(p => ({ ...p, left, caretShift: pos.anchor - left, settled: true }));
  }, [pos]);

  if (!isMobile || !label) return children;

  function showTooltip(e) {
    /* Several of these badges sit in rows that open a drawer when clicked. */
    e.stopPropagation();
    if (!wrapRef.current) return;
    const rect   = wrapRef.current.getBoundingClientRect();
    const anchor = rect.left + rect.width / 2;
    setPos({
      bottom: window.innerHeight - rect.top + 8,
      anchor,
      left: anchor,
      caretShift: 0,
      settled: false,
    });
  }

  return (
    <span ref={wrapRef} className={styles.wrap} onClick={showTooltip}>
      {children}
      {pos && createPortal(
        <div
          ref={bubbleRef}
          className={styles.tooltip}
          style={{ bottom: pos.bottom, left: pos.left }}
          role="tooltip"
        >
          {label}
          <span
            className={styles.caret}
            style={{ left: `calc(50% + ${pos.caretShift}px)` }}
          />
        </div>,
        document.body
      )}
    </span>
  );
}

export default StatusTooltip;
